import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useHarvestComponents, useCompanyDisabledHarvestComponentIds } from "@/hooks/useHarvestComponents";

type Props =
  | { mode: "company"; companyId: string | null; categories: string[] }
  | { mode: "default"; companyId?: undefined; categories: string[] };

/**
 * Brand × appliance-type harvest selection.
 * - default mode: edits platform defaults (brand_harvest_components, superadmin only).
 * - company mode: edits this company's include/exclude overrides on top of the defaults.
 */
export default function BrandHarvestSelector(props: Props) {
  const { mode, categories } = props;
  const companyId = props.mode === "company" ? props.companyId : null;
  const { toast } = useToast();
  const { t } = useTranslation("settings");
  const qc = useQueryClient();
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);

  const { data: components = [] } = useHarvestComponents();
  const { data: disabledIds = new Set<string>() } = useCompanyDisabledHarvestComponentIds();

  const { data: brands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const { data, error } = await supabase.from("brands").select("name").order("name");
      if (error) throw error;
      return data as { name: string }[];
    },
  });

  const ready = !!brand && !!category;

  const { data: defaults = [] } = useQuery({
    queryKey: ["brand_harvest_components", brand, category],
    enabled: ready,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_harvest_components")
        .select("id, component_id, appliance_type")
        .ilike("brand", brand)
        .or(`appliance_type.is.null,appliance_type.eq.${category}`);
      if (error) throw error;
      return data;
    },
  });

  const { data: overrides = [] } = useQuery({
    queryKey: ["company_brand_harvest_overrides", companyId, brand, category],
    enabled: ready && mode === "company" && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_brand_harvest_overrides")
        .select("id, component_id, mode")
        .eq("company_id", companyId!)
        .ilike("brand", brand)
        .eq("appliance_type", category);
      if (error) throw error;
      return data;
    },
  });

  const defaultIds = new Set(defaults.map((d) => d.component_id));
  const ovById = new Map(overrides.map((o) => [o.component_id, o]));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["brand_harvest_components"] });
    qc.invalidateQueries({ queryKey: ["company_brand_harvest_overrides"] });
    qc.invalidateQueries({ queryKey: ["harvest_components_for_brand"] });
    qc.invalidateQueries({ queryKey: ["harvest_recommendations"] });
  };
  const assertChanged = (data: unknown[] | null) => {
    if (!data || data.length === 0) throw new Error(t("brandHarvest.errNothingSaved"));
  };

  const setSelected = useMutation({
    mutationFn: async ({ componentId, on }: { componentId: string; on: boolean }) => {
      if (mode === "default") {
        if (on) {
          const { data, error } = await supabase.from("brand_harvest_components")
            .insert({ brand, component_id: componentId, appliance_type: category }).select("id");
          if (error) throw error; assertChanged(data);
        } else {
          const { data, error } = await supabase.from("brand_harvest_components").delete()
            .ilike("brand", brand).eq("component_id", componentId).eq("appliance_type", category).select("id");
          if (error) throw error; assertChanged(data);
        }
        return;
      }
      if (!companyId) throw new Error(t("brandHarvest.errNoCompany"));
      const existing = ovById.get(componentId);
      const isDefault = defaultIds.has(componentId);
      // Desired: on. Default → remove exclude. Non-default → add include.
      // Desired: off. Default → add exclude. Non-default → remove include.
      if (existing && ((on && existing.mode === "exclude") || (!on && existing.mode === "include"))) {
        const { data, error } = await supabase.from("company_brand_harvest_overrides").delete().eq("id", existing.id).select("id");
        if (error) throw error; assertChanged(data);
        return;
      }
      if ((on && !isDefault) || (!on && isDefault)) {
        const { data, error } = await supabase.from("company_brand_harvest_overrides").insert({
          company_id: companyId, brand, appliance_type: category, component_id: componentId,
          mode: on ? "include" : "exclude",
        }).select("id");
        if (error) throw error; assertChanged(data);
      }
    },
    onSuccess: () => { invalidate(); toast({ title: t("brandHarvest.toastSaved") }); },
    onError: (e: any) => toast({ title: t("brandHarvest.toastSaveFailed"), description: e.message, variant: "destructive" }),
  });

  const reset = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error(t("brandHarvest.errNoCompany"));
      const { data, error } = await supabase.from("company_brand_harvest_overrides").delete()
        .eq("company_id", companyId).ilike("brand", brand).eq("appliance_type", category).select("id");
      if (error) throw error; assertChanged(data);
    },
    onSuccess: () => { invalidate(); setConfirmReset(false); toast({ title: t("brandHarvest.toastReset") }); },
    onError: (e: any) => { setConfirmReset(false); toast({ title: t("brandHarvest.toastResetFailed"), description: e.message, variant: "destructive" }); },
  });

  const list = components.filter((c) =>
    (!c.appliance_category || c.appliance_category === category) &&
    (mode === "default" ? c.is_system_default : c.is_system_default || c.company_id === companyId),
  );

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">{t("brandHarvest.categoryLabel")}</label>
          <SearchableSelect
            value={category || null}
            onChange={(v) => { setCategory(v ?? ""); }}
            placeholder={t("brandHarvest.chooseCategory")}
            searchPlaceholder={t("brandHarvest.searchCategories")}
            emptyMessage={t("brandHarvest.noCategories")}
            items={categories.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">{t("brandHarvest.brandLabel")}</label>
          <SearchableSelect
            value={brand || null}
            onChange={(v) => setBrand(v ?? "")}
            placeholder={t("brandHarvest.chooseBrand")}
            searchPlaceholder={t("brandHarvest.searchBrands")}
            emptyMessage={t("brandHarvest.noBrands")}
            items={brands.map((b) => ({ value: b.name, label: b.name }))}
          />
        </div>
      </div>

      {ready && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-2">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-sm text-muted-foreground">
              {mode === "company" ? (
                <Trans t={t} i18nKey="brandHarvest.introCompany" values={{ brand, type: category }} components={{ b: <span className="font-medium text-foreground" /> }} />
              ) : (
                <Trans t={t} i18nKey="brandHarvest.introDefault" values={{ brand, type: category }} components={{ b: <span className="font-medium text-foreground" /> }} />
              )}
            </p>
            {mode === "company" && overrides.length > 0 && (
              <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => setConfirmReset(true)}>
                <RotateCcw className="w-3.5 h-3.5" /> {t("brandHarvest.resetButton")}
              </Button>
            )}
          </div>
          {list.length === 0 && <p className="text-sm text-muted-foreground">{t("brandHarvest.noComponents")}</p>}
          {list.map((c) => {
            const isDefault = defaultIds.has(c.id);
            const ov = ovById.get(c.id);
            const companyDisabled = mode === "company" && c.is_system_default && disabledIds.has(c.id);
            const selected = mode === "default"
              ? isDefault
              : ov ? ov.mode === "include" : isDefault;
            return (
              <div key={c.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border border-border ${companyDisabled ? "opacity-60" : ""}`}>
                <label className="flex items-center gap-3 cursor-pointer min-w-0">
                  <Checkbox
                    checked={selected && !companyDisabled}
                    disabled={setSelected.isPending || companyDisabled}
                    onCheckedChange={(v) => setSelected.mutate({ componentId: c.id, on: !!v })}
                  />
                  <span className="font-medium text-foreground text-sm truncate">{c.name}</span>
                  {!c.is_system_default && <Badge variant="outline">{t("brandHarvest.badgeCustom")}</Badge>}
                </label>
                <div className="flex gap-1.5 shrink-0">
                  {companyDisabled ? (
                    <Badge variant="secondary">{t("brandHarvest.badgeDisabled")}</Badge>
                  ) : mode === "company" ? (
                    ov?.mode === "include" ? <Badge>{t("brandHarvest.badgeAdded")}</Badge>
                    : ov?.mode === "exclude" ? <Badge variant="destructive">{t("brandHarvest.badgeRemoved")}</Badge>
                    : isDefault ? <Badge variant="secondary">{t("brandHarvest.badgePlatformDefault")}</Badge> : null
                  ) : isDefault ? <Badge variant="secondary">{t("brandHarvest.badgeDefault")}</Badge> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("brandHarvest.resetTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("brandHarvest.resetDescription", { brand, type: category })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => reset.mutate()} disabled={reset.isPending}>{t("brandHarvest.resetConfirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
